import type {
  CanvasNote, Goal, Habit, HabitLog, LifeSpace, Project, ProjectComment, RewardUsage, Section, Setting, Tag, Task, TaskComment, TaskReminder, TimeEntry, UserPreference,
} from '@/src/renderer/lib/db/models'
import {
  makeCanvasNote, makeGoal, makeHabit, makeHabitLog, makeProject, makeProjectComment, makeRewardUsage, makeSection, makeTag, makeTask, makeTaskComment, makeTaskReminder, makeTimeEntry,
} from '@/src/renderer/lib/dataSource/memoryDataSource'

/**
 * **Das Backup-Format** (`.happytask`, #158, ADR 0035): eine ZIP-Datei mit `manifest.json` und
 * `data.json`. Hier stehen die Typen, die Tabellen und was beim Einlesen je Tabelle zu tun ist.
 */

export const BACKUP_FORMAT = 'happytask-backup'
export const BACKUP_FORMAT_VERSION = 1
export const BACKUP_EXTENSION = 'happytask'

export const LIFE_SPACES: readonly LifeSpace[] = ['personal', 'work', 'home']

/** Was HappyTask ausmacht — je Zeile ein Lebensbereich, ein Projekt oder eine Aufgabe darüber. */
export const CONTENT_TABLES = [
  'tags', 'projects', 'sections', 'tasks', 'canvasNotes', 'taskComments', 'projectComments', 'taskReminders', 'timeEntries', 'goals', 'habits', 'habitLogs',
] as const
/** Der Knoten „Einstellungen“ im Auswahl-Baum. */
export const SETTINGS_TABLES = ['settings', 'userPreferences', 'rewardUsages'] as const

export type ContentTable = (typeof CONTENT_TABLES)[number]
export type SettingsTable = (typeof SETTINGS_TABLES)[number]
export type BackupTable = ContentTable | SettingsTable

export interface BackupTables {
  tags: Tag[]
  projects: Project[]
  sections: Section[]
  tasks: Task[]
  canvasNotes: CanvasNote[]
  taskComments: TaskComment[]
  projectComments: ProjectComment[]
  taskReminders: TaskReminder[]
  timeEntries: TimeEntry[]
  goals: Goal[]
  habits: Habit[]
  habitLogs: HabitLog[]
  settings: Setting[]
  userPreferences: UserPreference[]
  rewardUsages: RewardUsage[]
}

export type BackupRow = BackupTables[BackupTable][number]

/** Name, E-Mail, Avatar — nur als Auskunft (Entscheidung 7); das Wiederherstellen übernimmt sie nie. */
export interface BackupProfile {
  fullName: string | null
  displayName: string | null
  email: string | null
  avatarPresetId: string | null
}

/** Was eine Datenquelle für ein Backup liefert: alle Zeilen und das Profil. */
export interface BackupSnapshot {
  tables: BackupTables
  profile: BackupProfile | null
}

/** Der Inhalt von `data.json`. */
export interface BackupData extends BackupSnapshot {
  /** Das Konto, aus dem das Backup stammt; `null` ohne Konto (offline) — gilt dann überall als eigenes. */
  accountId: string | null
}

/** Wer das Backup geschrieben hat; liefert der Hauptprozess (`backup:device-info`). */
export interface BackupDeviceInfo {
  deviceId: string
  deviceName: string
  platform: string
  appVersion: string
}

export type BackupCounts = Partial<Record<LifeSpace, { projects: number; tasks: number; goals: number; habits: number }>>

/** Wie `data.enc` verschlüsselt ist (Entscheidung 17) — AES-256-GCM, Schlüssel per scrypt; Salz und IV in Base64. */
export interface BackupCryptoParams {
  cipher: 'aes-256-gcm'
  kdf: 'scrypt'
  salt: string
  iv: string
  N: number
  r: number
  p: number
}

/**
 * Der Inhalt von `manifest.json` — lesbar, ohne die Daten zu entpacken. Ein geschütztes Backup verrät
 * nur Datum, Gerät und Versionen: `accountId` ist dort `null`, `counts` leer, dazu `crypto`.
 */
export interface BackupManifest extends BackupDeviceInfo {
  format: typeof BACKUP_FORMAT
  formatVersion: number
  dbVersion: number
  /** ISO-Zeitpunkt. */
  exportedAt: string
  encrypted: boolean
  accountId: string | null
  counts: BackupCounts
  crypto?: BackupCryptoParams
}

export function emptyTables(): BackupTables {
  return {
    tags: [], projects: [], sections: [], tasks: [], canvasNotes: [], taskComments: [], projectComments: [], taskReminders: [], timeEntries: [],
    goals: [], habits: [], habitLogs: [], settings: [], userPreferences: [], rewardUsages: [],
  }
}

/**
 * **Einstellungen, die zum Gerät gehören** und nicht ins Backup: die Merker des Syncs
 * (`last…SyncTime`, `lastSyncRunAt`), was das Gerät beim Start braucht (Modus, Sprache, Speicherort) und
 * die Merker der Einführung (`intro.…`, #323: Stand je Gerät).
 */
export function isDeviceSettingKey(key: string): boolean {
  return /sync/i.test(key) || ['storageMode', 'mode', 'language', 'theme'].includes(key) || key.startsWith('intro.')
}

const BASE_DATES = ['createdAt', 'updatedAt', 'lastModified', 'syncedAt']

/**
 * **Datums-Felder je Tabelle** — feste Liste, keine Muster-Erkennung: Ein Titel, der wie ein
 * Datum aussieht, bleibt Text.
 */
export const DATE_FIELDS: Record<BackupTable, readonly string[]> = {
  tags: [...BASE_DATES, 'deletedAt'],
  projects: [...BASE_DATES, 'startDate', 'endDate', 'deletedAt'],
  sections: [...BASE_DATES, 'deletedAt', 'archivedAt'],
  tasks: [...BASE_DATES, 'dueDate', 'startDate', 'completedAt', 'deletedAt', 'pausedAt'],
  canvasNotes: [...BASE_DATES, 'convertedAt', 'deletedAt'],
  taskComments: [...BASE_DATES, 'postedAt', 'deletedAt'],
  projectComments: [...BASE_DATES, 'postedAt', 'deletedAt'],
  taskReminders: [...BASE_DATES, 'snoozedUntil', 'deletedAt'],
  timeEntries: [...BASE_DATES, 'startTime', 'endTime', 'deletedAt'],
  goals: [...BASE_DATES, 'deletedAt'],
  habits: [...BASE_DATES, 'startDate', 'pausedAt', 'deletedAt'],
  habitLogs: [...BASE_DATES, 'deletedAt'],
  settings: BASE_DATES,
  userPreferences: [...BASE_DATES, 'deletedAt'],
  rewardUsages: [...BASE_DATES, 'usedAt', 'deletedAt'],
}

type Normalizer<T> = (row: any) => T

/**
 * **Ein älteres Backup auf den heutigen Stand:** Je Tabelle füllt ein Normalisierer fehlende Felder
 * mit denselben Standards wie beim Neuanlegen. Die Datenbank-Migrationen laufen nicht mit — sie
 * wirken nur auf den Bestand.
 */
export const NORMALIZERS: { [K in BackupTable]: Normalizer<BackupTables[K][number]> } = {
  tags: row => makeTag(row),
  projects: row => makeProject(row),
  sections: row => makeSection(row),
  tasks: row => makeTask({ ...row, tags: Array.isArray(row.tags) ? row.tags : [] }),
  canvasNotes: row => makeCanvasNote(row),
  taskComments: row => makeTaskComment(row),
  projectComments: row => makeProjectComment(row),
  taskReminders: row => makeTaskReminder(row),
  timeEntries: row => makeTimeEntry(row),
  goals: row => makeGoal(row),
  habits: row => makeHabit(row),
  habitLogs: row => makeHabitLog(row),
  settings: row => ({ description: null, isUserSetting: true, type: 'string', ...row }),
  userPreferences: row => ({ userId: null, cloudId: null, deletedAt: null, key: row.id, ...row }),
  rewardUsages: row => makeRewardUsage(row),
}

/** Zählt je Lebensbereich, was lebt — für das Manifest und die Liste der letzten Backups. */
export function countByLifeSpace(tables: BackupTables): BackupCounts {
  const counts: BackupCounts = {}
  const bump = (lifeSpace: LifeSpace, kind: 'projects' | 'tasks' | 'goals' | 'habits') => {
    const entry = (counts[lifeSpace] ??= { projects: 0, tasks: 0, goals: 0, habits: 0 })
    entry[kind]++
  }
  for (const p of tables.projects) if (!p.deletedAt) bump(p.lifeSpace, 'projects')
  for (const t of tables.tasks) if (!t.deletedAt) bump(t.lifeSpace, 'tasks')
  for (const g of tables.goals) if (!g.deletedAt) bump(g.lifeSpace, 'goals')
  for (const h of tables.habits) if (!h.deletedAt) bump(h.lifeSpace, 'habits')
  return counts
}

/**
 * **HappyTask ist leer** (Entscheidung 2a): kein lebendes Projekt, keine lebende Aufgabe, kein Ziel,
 * keine Gewohnheit, in keinem Lebensbereich. Tags und Einstellungen zählen nicht.
 */
export function isEmptySnapshot(tables: Pick<BackupTables, 'projects' | 'tasks' | 'goals' | 'habits'>): boolean {
  return ![tables.projects, tables.tasks, tables.goals, tables.habits].some(rows => rows.some(row => !row.deletedAt))
}
